import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { usePrefs, useLabels, useRequestData } from 'wsm-common-data';
import { CommonModal } from 'wsm-common-modal';
import { Popover, Overlay } from 'react-bootstrap';
import useMedia from '../hooks/useMedia';
import { getLocationStatusProps } from '../utils/utility';

const LocationIconLabel = ({ icon, locationLabel }) => (
	<>
		<i
			className={`ddc-icon ddc-icon-${icon} mr-3 text-muted`}
			data-testid="location-status-icon"
		/>
		<span
			// eslint-disable-next-line react/no-danger
			dangerouslySetInnerHTML={{ __html: locationLabel }}
		/>
	</>
);

LocationIconLabel.propTypes = {
	icon: PropTypes.string.isRequired,
	locationLabel: PropTypes.string.isRequired
};

const LocationStatus = ({ locationName, status }) => {
	const { enableDealershipName, showVehicleStatus } = usePrefs();
	const { windowId, deviceType } = useRequestData();
	const labels = useLabels();
	const canHover = useMedia('(hover: hover) and (pointer: fine)', false);
	const triggerRef = useRef(null);
	const hideTimer = useRef(null);
	const isDesktop = deviceType === 'DESKTOP';

	const [isOpenModal, setOpenModal] = useState(false);
	const [show, setShow] = useState(false);

	const isEnableDealershipName = enableDealershipName === 'true';
	const isShowVehicleStatus = showVehicleStatus === 'true';
	const descId = `location-status-desc-${windowId}`;

	const {
		icon,
		locationLabel,
		plainLocationLabel,
		popoverContent,
		modalAriaLabel
	} = useMemo(
		() =>
			getLocationStatusProps(
				status,
				labels,
				isShowVehicleStatus,
				locationName
			),
		[status, labels, isShowVehicleStatus, locationName]
	);

	if (!isEnableDealershipName) return null;

	const toggleModal = () => setOpenModal((prev) => !prev);

	const openTooltip = () => {
		clearTimeout(hideTimer.current);
		setShow(true);
	};

	const hideTooltip = () => {
		setShow(false);
	};

	const scheduleClose = () => {
		clearTimeout(hideTimer.current);
		hideTimer.current = setTimeout(() => setShow(false), 150);
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Escape') {
			setShow(false);
		}
	};

	if (!popoverContent) {
		return (
			<div className="d-flex align-items-center">
				<LocationIconLabel icon={icon} locationLabel={locationLabel} />
			</div>
		);
	}

	if (!canHover || !isDesktop) {
		return (
			<>
				<button
					type="button"
					className="btn-unstyled d-flex align-items-center btn--tooltip"
					onClick={toggleModal}
					aria-label={plainLocationLabel}
				>
					<LocationIconLabel
						icon={icon}
						locationLabel={locationLabel}
					/>
				</button>
				<CommonModal
					title={plainLocationLabel}
					isOpen={isOpenModal}
					onClose={toggleModal}
					ariaLabel={modalAriaLabel}
				>
					<p>{popoverContent}</p>
				</CommonModal>
			</>
		);
	}

	return (
		<>
			<span
				ref={triggerRef}
				role="button"
				tabIndex="0"
				aria-label={plainLocationLabel}
				aria-describedby={descId}
				onFocus={openTooltip}
				onBlur={scheduleClose}
				onMouseOver={openTooltip}
				onMouseOut={scheduleClose}
				onKeyDown={handleKeyDown}
				data-testid="vehicle-status-location"
				className="pointer btn-unstyled"
			>
				<LocationIconLabel icon={icon} locationLabel={locationLabel} />
			</span>
			{/* Persistent SR-only description — always in the DOM, always referenced. */}
			<span id={descId} className="sr-only" aria-hidden>
				{popoverContent}
			</span>
			<Overlay
				show={show}
				target={triggerRef.current}
				placement="top"
				onHide={hideTooltip}
			>
				<Popover
					id={`location-status-popover-${windowId}`}
					className="ws-vehicle-location-status-tooltip"
					role="tooltip"
					onMouseOver={openTooltip}
					onMouseOut={scheduleClose}
				>
					<span>{popoverContent}</span>
				</Popover>
			</Overlay>
		</>
	);
};

LocationStatus.propTypes = {
	locationName: PropTypes.string.isRequired,
	status: PropTypes.string
};

export default LocationStatus;
